import { WORKFLOW_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { cn, Icon, Kbd } from "@sagot/ui";
import { useId, type KeyboardEvent, type ReactNode } from "react";
import type { ExitRef } from "../exit-slots";
import { useConnectors } from "./connector-context";

const K = "engage.workflows.connectors";
const ITEM = "flex min-h-9 w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-sunken";

interface ConnectionMenuProps {
  source: ExitRef;
  /** The path the exit leads to now, if any. */
  target: string | undefined;
  /** Goes to a path; the handle must not take focus back when the menu closes. */
  onGoTo: (tileId: string) => void;
  onClose: () => void;
}

interface ItemProps {
  onSelect: () => void;
  disabledReason?: string;
  current?: boolean;
  children: ReactNode;
}

/** A menu button that stays focusable when it cannot be used (`aria-disabled`) and then says why. */
function Item({ onSelect, disabledReason, current, children }: ItemProps) {
  return (
    <button
      type="button"
      className={cn(ITEM, disabledReason && "cursor-not-allowed text-fg-subtle")}
      aria-disabled={disabledReason ? true : undefined}
      aria-current={current ? "true" : undefined}
      onClick={() => { if (!disabledReason) onSelect(); }}
    >
      <span className="flex min-w-0 items-center gap-2">{children}</span>
      {disabledReason ? <span className="shrink-0 text-xs">{disabledReason}</span> : null}
    </button>
  );
}

/**
 * What can be done with one exit: go to the path it leads to, connect it to another (the keyboard equivalent of dragging),
 * or delete the connection. Arrow keys move through the buttons; Del or Backspace deletes the connection.
 */
export function ConnectionMenu({ source, target, onGoTo, onClose }: ConnectionMenuProps) {
  const { t } = useTranslation();
  const api = useConnectors();
  const headingId = useId();
  const connectedTo = target !== undefined && api.hasPath(target) ? target : undefined;
  const done = (action: () => void) => () => {
    action();
    onClose();
  };
  const reasonOf = (tileId: string | null): string | undefined => {
    const check = api.check(source, tileId);
    return check.ok ? undefined : t(`${K}.reasons.${check.reason}`, { max: WORKFLOW_LIMITS.paths });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === "Delete" || event.key === "Backspace") && connectedTo && api.editable) {
      event.preventDefault();
      done(() => { api.disconnect(source); })();
      return;
    }
    const buttons = [...event.currentTarget.querySelectorAll<HTMLButtonElement>("button")];
    const at = buttons.findIndex((button) => button === document.activeElement);
    const next = { ArrowDown: at + 1, ArrowUp: at - 1, Home: 0, End: buttons.length - 1 }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    buttons[Math.min(Math.max(next, 0), buttons.length - 1)]?.focus();
  };

  return (
    // The arrow keys move between the buttons inside; the keydown handler is only a roving-focus helper for them.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="space-y-1" data-connection-menu="" onKeyDown={onKeyDown}>
      {connectedTo ? (
        <Item onSelect={done(() => { onGoTo(connectedTo); })}>
          <Icon name="arrow-right" className="size-4 shrink-0 text-primary-ink" />
          <span className="truncate">{t(`${K}.menu.goTo`, { path: api.pathTitle(connectedTo) })}</span>
        </Item>
      ) : null}
      {api.editable ? (
        <>
          <p id={headingId} className="px-2 pt-1 text-[11px] font-bold uppercase tracking-wider text-fg-subtle">{t(`${K}.menu.connectTo`)}</p>
          <ul aria-labelledby={headingId} className="max-h-60 overflow-y-auto">
            <li>
              <Item disabledReason={reasonOf(null)} onSelect={done(() => { api.createPath(source); })}>
                <Icon name="plus" className="size-4 shrink-0 text-primary-ink" />
                <span>{t(`${K}.menu.newPath`)}</span>
              </Item>
            </li>
            {api.pathIds.filter((id) => id !== source.path).map((id) => (
              <li key={id}>
                <Item current={id === connectedTo} disabledReason={id === connectedTo ? undefined : reasonOf(id)} onSelect={done(() => { api.connect(source, id); })}>
                  {id === connectedTo ? <Icon name="check" className="size-4 shrink-0 text-primary-ink" /> : <span className="size-4 shrink-0" aria-hidden="true" />}
                  <span className="truncate">{api.pathTitle(id)}</span>
                  {id === connectedTo ? <span className="shrink-0 text-xs text-fg-subtle">{t(`${K}.menu.connected`)}</span> : null}
                </Item>
              </li>
            ))}
          </ul>
          {connectedTo ? (
            <button type="button" className={cn(ITEM, "border-t border-line text-danger")} aria-keyshortcuts="Delete Backspace" onClick={done(() => { api.disconnect(source); })}>
              <span className="flex items-center gap-2">
                <Icon name="trash" className="size-4 shrink-0" />
                {t(`${K}.menu.delete`)}
              </span>
              <Kbd aria-hidden="true">{t(`${K}.menu.deleteKeys`)}</Kbd>
            </button>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
